import { useState } from "react";
import { toast } from "sonner";
import { Copy, Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api } from "@/lib/api";
import { EMAIL_RE } from "@/lib/format";

const ROLES = [
  { key: "moderator", label: "Moderator", text: "Verification, Reports and Appeals, plus user view, warn, hide and ban." },
  { key: "support", label: "Support", text: "User view, export, resend verification, short tier grants, notes, and the waitlist." },
];

export function TempPasswordDialog({ value, onClose }) {
  const copy = () => navigator.clipboard?.writeText(value.password).then(() => toast.success("Copied."));
  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-[20px]" data-testid="temp-password-dialog">
        <DialogHeader>
          <DialogTitle>Temporary password</DialogTitle>
          <DialogDescription>Share it privately with {value?.email}. It’s shown only once. They should change it after signing in.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 rounded-xl bg-[var(--cw-surface-tertiary)] p-3">
          <code className="flex-1 break-all text-sm" data-testid="temp-password-value">{value?.password}</code>
          <Button size="icon" variant="ghost" onClick={copy} aria-label="Copy password" data-testid="temp-password-copy-button"><Copy /></Button>
        </div>
        <DialogFooter><Button onClick={onClose} data-testid="temp-password-done-button">Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CreateAdminDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "moderator", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = EMAIL_RE.test(form.email.trim());

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api("/admin/admins", { body: { email: form.email.trim(), role: form.role, name: form.name.trim() || null, password: form.password || null } });
      setOpen(false);
      setForm({ email: "", name: "", role: "moderator", password: "" });
      onCreated(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="admin-create-open-button"><Plus />Invite admin</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-[20px]" data-testid="admin-create-dialog">
          <form onSubmit={submit} className="space-y-5">
            <DialogHeader><DialogTitle>Invite an admin</DialogTitle><DialogDescription>We email them an invite (Content: team_invite_email). If no email goes out, you’ll see a temporary password once.</DialogDescription></DialogHeader>
            <div><Label htmlFor="na-email">Email</Label><Input id="na-email" type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} className="mt-2" data-testid="admin-create-email-input" /></div>
            <div><Label htmlFor="na-name">Name (optional)</Label><Input id="na-name" value={form.name} onChange={(e) => set("name")(e.target.value)} className="mt-2" data-testid="admin-create-name-input" /></div>
            <fieldset>
              <legend className="text-sm font-medium">Role</legend>
              <RadioGroup value={form.role} onValueChange={set("role")} className="mt-2 gap-2">
                {ROLES.map((r) => (
                  <label key={r.key} htmlFor={`role-${r.key}`} className={`flex cursor-pointer gap-3 rounded-xl border p-3 transition-colors ${form.role === r.key ? "border-primary cw-bg-tint" : "border-border"}`}>
                    <RadioGroupItem id={`role-${r.key}`} value={r.key} className="mt-0.5" data-testid={`admin-create-role-${r.key}`} />
                    <span><span className="block text-sm font-medium">{r.label}</span><span className="cw-soft block text-xs leading-5">{r.text}</span></span>
                  </label>
                ))}
              </RadioGroup>
            </fieldset>
            <div><Label htmlFor="na-pw">Password (optional)</Label>
              <Input id="na-pw" type="password" autoComplete="new-password" value={form.password} onChange={(e) => set("password")(e.target.value)} className="mt-2" data-testid="admin-create-password-input" />
              <p className="cw-muted mt-2 text-xs">Leave empty to generate a temporary password. Otherwise 12+ characters with upper, lower and a number.</p></div>
            {error && <p className="cw-error text-sm" role="alert" data-testid="admin-create-error">{error}</p>}
            <DialogFooter><Button type="submit" disabled={busy || !valid} data-testid="admin-create-submit-button">{busy ? "Inviting…" : "Send invite"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
